import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { SecurityProfileQuiz } from '@/client/security-profile';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { LanguageContext } from '@/common/context/LanguageContext';
import {
  SecurityProfileQuizComponent,
  SecurityProfileSummary,
} from '@/components/security-profile';
import { useAccount, useSecurityProfileQuiz } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import { determineAttemptLanguage } from '@/utils/helpers/language.helper';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AccountHeader } from './AccountHeader';

const StyledSecurityProfileContainer = styled(FlexContainer)`
  width: 100%;
`;

const StyledTwoColumnLayout = styled(FlexContainer)`
  gap: var(--big-padding);
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--default-padding);

    .welcome-panel {
      flex: none;
      width: 100%;
    }
  }
`;

const pathItems = (
  account: Account,
  currentAccount: Account,
  t: (key: string, placeholder?: { name: string }) => string,
) => {
  const items: MenuItem[] = [
    {
      label: currentAccount?.name,
      url: !currentAccount?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](currentAccount?.id)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount?.id),
      className: 'active',
      template: AppBreadCrumbTemplate,
    },
  ];

  if (currentAccount?.isSystem && account) {
    items.push(
      {
        label: t('accounts'),
        url: RedirectPaths[RedirectPathsEnum.ACCOUNTS](),
        template: AppBreadCrumbTemplate,
      },
      {
        label: account?.name,
        url: !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
          : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
        className: 'active',
        template: AppBreadCrumbTemplate,
      },
    );
  }

  items.push({
    label: t('securityProfile.title'),
    url: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_SECURITY_PROFILE](account?.id)
      : RedirectPaths[RedirectPathsEnum.ACCOUNT_SECURITY_PROFILE](),
    template: AppBreadCrumbTemplate,
  });

  return items;
};

export const AccountSecurityProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { id } = useParams();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const accountId = currentAccount?.isSystem ? id : currentAccount?.id;
  const { account } = useAccount({ accountId });
  const { language } = useContext(LanguageContext);

  const urlLanguage = new URLSearchParams(location.search).get('language');

  const [showSummary, setShowSummary] = useState(false);
  const [editQuestionIndex, setEditQuestionIndex] = useState<
    number | undefined
  >(undefined);

  const { quiz, isLoading } = useSecurityProfileQuiz(accountId || '');

  const attemptLanguage = useMemo(
    () =>
      determineAttemptLanguage(
        language,
        quiz?.supportedLanguages || [],
        urlLanguage,
      ),
    [language, quiz?.supportedLanguages, urlLanguage],
  );

  useEffect(() => {
    if (quiz?.isCompleted) {
      setShowSummary(true);
    }
  }, [quiz?.isCompleted]);

  if (!currentAccount || !account) {
    return (
      <FlexContainer direction="column" className="mt-5">
        <ProgressSpinner />
      </FlexContainer>
    );
  }

  if (isLoading) {
    return (
      <FlexContainer direction="column" align="center" className="mt-5">
        <ProgressSpinner />
        <span className="ml-2">{t('securityProfile.loading')}</span>
      </FlexContainer>
    );
  }

  return (
    <FlexContainer direction="column" align="flex-start">
      <div className="w-full">
        <AppBreadCrumb
          model={pathItems(account as Account, currentAccount as Account, t)}
        />
        <AccountHeader
          selectedAccount={account}
          title={t('securityProfile.title')}
        />

        <StyledSecurityProfileContainer>
          {showSummary ? (
            <SecurityProfileSummary
              quiz={quiz as SecurityProfileQuiz}
              responses={quiz?.responses || []}
              attemptLanguage={attemptLanguage}
              onEditQuestion={(questionIndex) => {
                setShowSummary(false);
                setEditQuestionIndex(questionIndex);
              }}
            />
          ) : (
            <StyledTwoColumnLayout>
              {quiz && account && (
                <SecurityProfileQuizComponent
                  quiz={quiz}
                  accountId={account.id}
                  attemptLanguage={attemptLanguage}
                  editQuestionIndex={editQuestionIndex}
                  onComplete={() => {
                    setShowSummary(true);
                    setEditQuestionIndex(undefined);
                  }}
                  onShowSummary={() => {
                    setShowSummary(true);
                    setEditQuestionIndex(undefined);
                  }}
                  onCancel={() => {
                    setShowSummary(true);
                    setEditQuestionIndex(undefined);
                  }}
                />
              )}
            </StyledTwoColumnLayout>
          )}
        </StyledSecurityProfileContainer>
      </div>
    </FlexContainer>
  );
};
